/**
 * Sănătate sistem — costuri API, procesare documente, incidente software.
 *
 * De ce există (cerut de Dan, 19 aug 2026): problemele de SOFT stăteau la
 * grămadă cu alertele de fabrică, iar 199 de notificări tehnice necitite
 * îngropau exact ce conta — regresia AI din 16 aug care semnala moartea
 * escaladării la Claude. Costul API nu se vedea nicăieri: 148 USD arși în două
 * zile într-o buclă de reîncercări, descoperiți abia din factură.
 */

import { api, $, toast, escHtml } from './core';
import { askConfirm } from './dialogs';

interface Sanatate {
    costuri: {
        cosuri?: Record<string, { eticheta: string; cheltuit_usd: number; limita_usd: number;
                                  ramas_usd: number; pct: number; apeluri: number; epuizat: boolean }>;
        luna_usd: number | null; plafon_usd: number | null; ramas_usd: number | null;
        pe_zile: Array<{ ziua: string; usd: number; apeluri: number }>;
        pe_scop: Array<{ scop: string; usd: number; apeluri: number }>;
        hub: { azi: number; luna: number; apeluri_azi: number } | null;
    };
    procesare: Record<string, any>;
    incidente: {
        grupuri: Array<{ tip: string; eticheta: string; total: number; necitite: number; ultima: string; severitate: string; categorie: string }>;
        recente: Array<{ id: number; tip: string; eticheta: string; titlu: string; corp: string; severitate: string; created_at: string; ultima_la?: string | null; read_at: string | null; categorie: string;
            /** 11 oct 2026: închisă de scripts/alerte_inchidere_automata.py (verificarea n-o mai confirmă); gol = de om */
            inchisa_motiv?: string | null }>;
    };
    /** 11 oct 2026: utilajele care au în manual alarme prin care cer mentenanță și dacă le vedem */
    mentenanta_masini?: Array<{ machine_id: string; nume: string; stare: 'neconectat' | 'ceas' | 'alarme' | 'alarme_si_ceas';
        coduri_manual: number; exemple: string[]; cereri_primite: number; ultima_cerere: string | null }>;
}

let doarNecitite = false;

function bani(v: number | null | undefined): string {
    return (v === null || v === undefined) ? '—' : `${Number(v).toFixed(2)} $`;
}

function cardCost(d: Sanatate['costuri']): string {
    const folosit = d.luna_usd || 0, plafon = d.plafon_usd || 0;
    const procent = plafon ? Math.min(100, (folosit / plafon) * 100) : 0;
    const culoare = procent > 85 ? 'var(--danger,#e05252)' : procent > 60 ? '#d9a441' : 'var(--ok,#4caf50)';
    const maxZi = Math.max(1, ...d.pe_zile.map(z => z.usd));
    const bare = d.pe_zile.slice().reverse().map(z => {
        const h = Math.max(2, Math.round((z.usd / maxZi) * 56));
        return `<div class="ss-bara" title="${escHtml(z.ziua)}: ${z.usd} $ din ${z.apeluri} apeluri">
            <div style="height:${h}px"></div><span>${escHtml(z.ziua.slice(8))}</span></div>`;
    }).join('');
    const scopuri = d.pe_scop.map(s => `<tr><td>${escHtml(s.scop)}</td>
        <td class="ss-num">${s.apeluri}</td><td class="ss-num">${s.usd.toFixed(2)} $</td></tr>`).join('')
        || '<tr><td colspan="3" class="ss-gol">nimic în ultimele 7 zile</td></tr>';
    const hub = d.hub ? `<div class="ss-hub">
            <span>Hub (copilot router, cheie separată)</span>
            <b>${bani(d.hub.luna)}</b> luna asta · ${bani(d.hub.azi)} azi · ${d.hub.apeluri_azi} apeluri</div>` : '';
    const cosuri = Object.entries(d.cosuri || {}).map(([cheie, c]) => {
        const culoare = c.epuizat ? 'var(--danger,#e05252)' : c.pct > 80 ? '#d9a441' : 'var(--ok,#4caf50)';
        return `<div class="ss-cos${c.epuizat ? ' epuizat' : ''}">
            <div class="ss-cos-cap">
                <b>${escHtml(c.eticheta)}</b>
                <span class="ss-sub">${c.cheltuit_usd.toFixed(2)} $ din
                    <input class="ss-plafon" type="number" min="0" max="5000" step="5"
                           value="${c.limita_usd}" data-cos="${escHtml(cheie)}"
                           title="Schimbă plafonul lunar al acestui coș"> $</span>
            </div>
            <div class="ss-progres"><div style="width:${c.pct}%;background:${culoare}"></div></div>
            <div class="ss-sub">${c.apeluri} apeluri · ${c.epuizat
                ? '<b style="color:var(--danger,#e05252)">EPUIZAT — se oprește doar acest coș</b>'
                : `mai are ${c.ramas_usd.toFixed(2)} $`}</div>
        </div>`;
    }).join('');
    return `<div class="ss-card">
        <h3>💳 Costuri API — fabrică</h3>
        <div class="ss-cosuri">${cosuri}</div>
        <div class="ss-mare">${bani(d.luna_usd)} <small>din
            <input class="ss-plafon ss-plafon-total" type="number" min="0" max="5000" step="10"
                   value="${d.plafon_usd ?? 0}" data-cos="total"
                   title="Plafonul TOTAL pe lună — cel care oprește tot. Se poate schimba de aici; nu mai cere acces pe server."> $
            pe lună — DOAR fabrica</small></div>
        ${d.hub && d.hub.luna != null ? `<div class="ss-sub" style="margin-top:2px">
            Total real pe cont, cu Hub: <b>${bani((d.luna_usd || 0) + (d.hub.luna || 0))}</b> luna asta.
            Plafonul de mai sus oprește DOAR apelurile fabricii; cele din Hub merg pe altă cheie și nu sunt tăiate de el.</div>` : ''}
        <div class="ss-progres"><div style="width:${procent}%;background:${culoare}"></div></div>
        <div class="ss-grafic">${bare}</div>
        <div class="ss-sub">consum zilnic, ultimele 2 săptămâni</div>
        <table class="ss-tabel"><thead><tr><th>pe ce s-au dus (7 zile)</th><th class="ss-num">apeluri</th><th class="ss-num">cost</th></tr></thead>
        <tbody>${scopuri}</tbody></table>
        ${hub}
    </div>`;
}

function cardProcesare(p: Record<string, any>): string {
    const cifre: Array<[string, any, boolean]> = [
        ['în coadă', p.in_coada, false],
        ['în lucru', p.in_lucru, false],
        ['fără index AI', p.fara_index, (p.fara_index || 0) > 0],
        ['eșuate', p.esuate, (p.esuate || 0) > 0],
        ['dubluri sărite', p.dubluri, false],
        ['procesate 7 zile', `${p.procesate_7z || 0} doc / ${p.pagini_7z || 0} pag`, false],
    ];
    const casete = cifre.map(([et, val, rau]) =>
        `<div class="ss-cifra${rau ? ' rau' : ''}"><b>${escHtml(String(val ?? '—'))}</b><span>${escHtml(et)}</span></div>`).join('');
    const probleme = (p.probleme || []).map((d: any) =>
        `<tr><td>#${d.id}</td><td>${escHtml(d.machine_id || '')}</td>
         <td class="ss-trunchiat">${escHtml(d.filename || '')}</td>
         <td><span class="ss-eticheta rau">${escHtml(d.processing_status)}</span></td></tr>`).join('')
        || '<tr><td colspan="4" class="ss-gol">niciun document cu probleme ✓</td></tr>';
    return `<div class="ss-card">
        <h3>📄 Procesare documente</h3>
        <div class="ss-cifre">${casete}</div>
        <table class="ss-tabel"><thead><tr><th>doc</th><th>utilaj</th><th>fișier</th><th>stare</th></tr></thead>
        <tbody>${probleme}</tbody></table>
        <button class="btn ss-actiune" id="ss-reindex">Reindexează documentele lipsă din memoria AI</button>
    </div>`;
}

function cardIncidente(inc: Sanatate['incidente']): string {
    /* Pe categorii, nu la grămadă (19 sep 2026, întrebarea lui Dan: «alarmele de
       la mașini și cu cele de la aplicație sunt la grămadă?»). Șase tipuri de
       aici sunt despre FABRICĂ — mașină fără date, colector mort, ceas nesincron
       — și stăteau printre cele despre model, documente și costuri, sub un titlu
       care spunea «doar probleme tehnice». Categoria vine de la server
       (`routes/sanatate_sistem.py: categoria()`), ca să fie un singur loc unde
       se hotărăște, același cu al panoului mare de alarme. */
    const randGrup = (g: Sanatate['incidente']['grupuri'][0]) => `
        <div class="ss-grup${g.necitite ? ' activ' : ''}">
            <div><b>${escHtml(g.eticheta)}</b><span class="ss-sub">ultima: ${escHtml((g.ultima || '').slice(0, 16))}</span></div>
            <div class="ss-grup-dr">
                <span class="ss-eticheta${g.necitite ? ' rau' : ''}">${g.necitite} necitite</span>
                <span class="ss-sub">${g.total} total</span>
                ${g.necitite ? `<button class="ss-mic" data-citeste-tip="${escHtml(g.tip)}">marchează citite</button>` : ''}
            </div>
        </div>`;
    const peCategorii = (cat: string, titlu: string) => {
        const lot = inc.grupuri.filter(g => (g.categorie || 'aplicatie') === cat);
        if (!lot.length) return '';
        const necitite = lot.reduce((s, g) => s + (g.necitite || 0), 0);
        return `<div class="ss-categorie">
            <div class="ss-categorie-cap">${escHtml(titlu)}
                <span class="ss-eticheta${necitite ? ' rau' : ''}">${necitite} necitite</span></div>
            ${lot.map(randGrup).join('')}
        </div>`;
    };
    const grupuri = (peCategorii('utilaje', '🏭 Utilaje și colectoare')
                   + peCategorii('aplicatie', '💻 Aplicație și AI'))
                   || '<div class="ss-gol">niciun incident înregistrat ✓</div>';
    /* Corpul era tăiat la trei rânduri, fără nicio cale de a-l deschide, iar
       cardul nu răspundea la clic (Dan, 21 aug: «apas pe ea și nu se întâmplă
       nimic, nu pot citi tot ce scrie»). Mesajele santinelelor sunt tocmai
       lucrul de citit: acolo scrie CE s-a stricat. Acum întregul card e un
       buton — se deschide, arată tot, și se marchează citit la prima
       deschidere, ca insigna să însemne ceva. */
    const recente = inc.recente.map(n => `
        <div class="ss-incident sev-${escHtml(n.severitate || 'info')}${n.read_at ? ' citit' : ''}"
             data-incident="${n.id}" role="button" tabindex="0"
             aria-expanded="false" title="clic pentru textul întreg">
            <div class="ss-incident-cap">
                <span class="ss-eticheta ss-cat-${escHtml(n.categorie || 'aplicatie')}"
                      title="${n.categorie === 'utilaje' ? 'despre utilaje' : 'despre aplicație'}"
                      >${n.categorie === 'utilaje' ? '🏭' : '💻'} ${escHtml(n.eticheta)}</span>
                <b>${escHtml(n.titlu || '')}</b>
                <span class="ss-sub">${n.ultima_la
                    ? `ultima: ${escHtml(n.ultima_la.slice(0, 16))} · prima: ${escHtml((n.created_at || '').slice(0, 16))}`
                    : escHtml((n.created_at || '').slice(0, 16))}</span>
                <span class="ss-sageata">▾</span>
            </div>
            <div class="ss-incident-corp">${escHtml(n.corp || '')}${n.inchisa_motiv
                ? `\n\n✓ Închisă automat: ${escHtml(n.inchisa_motiv)}` : ''}</div>
        </div>`).join('') || '<div class="ss-gol">nimic de arătat</div>';
    return `<div class="ss-card">
        <h3>🛠 Incidente de sistem</h3>
        <div class="ss-sub">Ce scriu veghetorii aplicației, pe două categorii. Scria aici, până
        pe 19 sep 2026, «doar probleme tehnice — alertele de utilaj rămân la clopoțel»: nu era
        adevărat, șase tipuri de mai jos sunt despre fabrică. Aceleași alarme se văd și în
        panoul mare de alarme (butonul ⚠ din antet), lângă cele venite de la mașini.</div>
        <div class="ss-grupuri">${grupuri}</div>
        <div class="ss-filtru">
            <label><input type="checkbox" id="ss-necitite" ${doarNecitite ? 'checked' : ''}> doar necitite</label>
        </div>
        <div class="ss-incidente">${recente}</div>
    </div>`;
}

/** Mentenanța cerută de mașini (11 oct 2026): alarmele „Întreținere programată 180H”, Sumitomo 7425… se recunosc
 *  singure și se leagă de lucrările noastre — dar numai la utilajele care își trimit alarmele. Cardul arată unde
 *  nu ajungem încă, ca un utilaj nou sau neconectat să nu rămână nevăzut. */
function cardMentenantaMasini(lista: Sanatate['mentenanta_masini']): string {
    if (!lista || !lista.length) return '';
    const stari: Record<string, [string, string]> = {
        neconectat: ['rau', 'nu trimite alarme'],
        ceas: ['atentie', 'doar ceasul de mentenanță'],
        alarme: ['bun', 'trimite alarmele'],
        alarme_si_ceas: ['bun', 'alarme + ceas'],
    };
    const randuri = lista.map(x => {
        const [cls, txt] = stari[x.stare] || ['', x.stare];
        return `<tr><td>${escHtml(x.nume)}</td>
            <td><span class="ss-eticheta ${cls}">${escHtml(txt)}</span></td>
            <td class="ss-num">${x.coduri_manual || '—'}</td>
            <td class="ss-trunchiat">${escHtml(x.exemple.slice(0, 4).join(', '))}</td>
            <td class="ss-num">${x.cereri_primite || 0}</td></tr>`;
    }).join('');
    const neconectate = lista.filter(x => x.stare === 'neconectat').length;
    return `<div class="ss-card">
        <h3>🔧 Mentenanța cerută de mașini</h3>
        <div class="ss-sub">Alarmele prin care un utilaj cere mentenanță se recunosc singure și se leagă de lucrările
            noastre cu aceeași cadență. ${neconectate ? `${neconectate} utilaje le au în manual, dar nu-și trimit încă alarmele.` : ''}</div>
        <div class="ss-tabel-gazda"><table class="ss-tabel"><thead><tr><th>utilaj</th><th>le vedem?</th>
            <th class="ss-num">coduri în manual</th><th>de exemplu</th><th class="ss-num">cereri primite</th></tr></thead>
        <tbody>${randuri}</tbody></table></div>
    </div>`;
}


async function reincarca(): Promise<void> {
    const gazda = $('#ss-continut');
    if (!gazda) return;
    gazda.innerHTML = '<div class="ss-gol">se încarcă…</div>';
    try {
        const d: Sanatate = await api(`/api/sistem/sanatate${doarNecitite ? '?necitite=1' : ''}`);
        gazda.innerHTML = cardCost(d.costuri) + cardProcesare(d.procesare) + cardIncidente(d.incidente)
            + cardMentenantaMasini(d.mentenanta_masini);
        leagaButoane();
    } catch (e: any) {
        gazda.innerHTML = `<div class="ss-gol">Nu am putut citi starea sistemului: ${escHtml(String(e?.message || e))}</div>`;
    }
}

function leagaButoane(): void {
    document.querySelectorAll<HTMLElement>('[data-citeste-tip]').forEach(b => {
        b.onclick = async () => {
            try {
                const r = await api('/api/sistem/incidente/citeste', {
                    method: 'POST', body: JSON.stringify({ tip: b.dataset.citesteTip }),
                });
                toast(`${r.marcate} notificări marcate citite`, 'success');
                reincarca();
            } catch (e: any) { toast('Nu am putut marca: ' + (e?.message || e), 'error'); }
        };
    });
    document.querySelectorAll<HTMLElement>('[data-incident]').forEach(card => {
        const deschide = async () => {
            const eraDeschis = card.classList.toggle('deschis');
            card.setAttribute('aria-expanded', String(eraDeschis));
            const sageata = card.querySelector('.ss-sageata');
            if (sageata) sageata.textContent = eraDeschis ? '▴' : '▾';
            // prima deschidere = citit; altfel insigna arată alarme pe care
            // omul tocmai le-a citit
            if (eraDeschis && !card.classList.contains('citit')) {
                card.classList.add('citit');
                try {
                    await api('/api/sistem/incidente/citeste', {
                        method: 'POST',
                        body: JSON.stringify({ ids: [Number(card.dataset.incident)] }),
                    });
                    void actualizeazaInsignaIncidente();
                } catch { /* citirea rămâne vizuală chiar dacă marcajul cade */ }
            }
        };
        card.onclick = () => { void deschide(); };
        card.onkeydown = (ev: KeyboardEvent) => {
            if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); void deschide(); }
        };
    });
    document.querySelectorAll<HTMLInputElement>('.ss-plafon').forEach(inp => {
        inp.onchange = async () => {
            const cos = inp.dataset.cos;
            const val = parseFloat(inp.value);
            if (!cos || !isFinite(val)) return;
            try {
                await api('/api/sistem/buget', {
                    method: 'POST', body: JSON.stringify({ cos, limita_usd: val }),
                });
                toast(cos === 'total'
                    ? `Plafon TOTAL salvat: ${val} $ pe lună`
                    : `Plafon salvat: ${val} $ pentru „${cos}"`, 'success');
                reincarca();
            } catch (e: any) {
                toast('Nu am putut salva plafonul: ' + (e?.message || e), 'error');
                reincarca();
            }
        };
    });
    const bif = $('#ss-necitite') as HTMLInputElement | null;
    if (bif) bif.onchange = () => { doarNecitite = bif.checked; reincarca(); };
    // 1 nov 2026 (Dan, auditul alertelor): butonul doar afișa comanda de rulat pe server — apăsat, nu făcea nimic.
    // Acum cere chiar reindexarea incrementală (aceeași ca cea de noapte, 02:15): scoate din memoria AI ce s-a
    // dezactivat sau șters și adaugă ce e nou în bază. Nu e reindexarea completă din Chat AI (aceea ține ore).
    const reidx = $('#ss-reindex') as HTMLButtonElement | null;
    if (reidx) reidx.onclick = async () => {
        if (!await askConfirm('Reindexez memoria AI?',
            'Adaug în memoria AI ce e nou în bază și scot ce s-a dezactivat — la fel ca reindexarea de noapte. '
            + 'Durează de obicei câteva minute.', 'Reindexează')) return;
        const eticheta = reidx.textContent;
        reidx.disabled = true;
        reidx.textContent = 'Reindexez… (câteva minute)';
        try {
            const r = await fetch('/api/ai_local/reindex', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ mode: 'incremental' }),
            });
            const d = await r.json().catch(() => ({}));
            if (r.ok && d.ok) {
                const st = (d.stats || {}) as { new_documents?: number; purge?: { sterse?: number } | string };
                const noi = Number(st.new_documents || 0);
                const scoase = typeof st.purge === 'object' && st.purge ? Number(st.purge.sterse || 0) : 0;
                toast(noi || scoase ? `Memoria AI e la zi: ${noi} fragmente noi, ${scoase} scoase`
                    : 'Memoria AI era deja la zi — nimic nou de adăugat', 'success');
            } else {
                toast(`Reindexarea n-a mers: ${d.error || ('HTTP ' + r.status)}`, 'error');
            }
        } catch (e) {
            toast(`Reindexarea n-a mers: ${String(e).slice(0, 120)}`, 'error');
        } finally {
            reidx.disabled = false;
            reidx.textContent = eticheta;
            reincarca();
        }
    };
}

export function initSistemSanatate(): void {
    const gazda = $('#ss-continut');
    if (!gazda) return;
    reincarca();
}


/** Insigna din meniu: câte incidente de sistem sunt necitite.
 *
 * Fără ea, alarmele stau într-o pagină pe care trebuie s-o deschizi anume ca
 * să afli că ai de ce s-o deschizi. Pe 21 aug erau 26 de alarme necitite de la
 * veghetorul problemelor deschise — scrise zilnic, văzute de nimeni.
 */
export async function actualizeazaInsignaIncidente(): Promise<void> {
    const insigna = document.getElementById('incidente-badge');
    if (!insigna) return;
    try {
        const r = await fetch('/api/sistem/incidente_necitite');
        if (!r.ok) { insigna.style.display = 'none'; return; }
        const d = await r.json();
        const n = Number(d.necitite || 0);
        if (n > 0) {
            insigna.textContent = String(n);
            const rest = Number(d.necitite_total || 0);
            insigna.title = [
                `${n} incidente necitite în ultimele 7 zile`,
                d.critice ? `${d.critice} critice` : '',
                rest > n ? `${rest} necitite în total (restul, mai vechi, în pagină)` : '',
            ].filter(Boolean).join(' · ');
            (insigna as HTMLElement).style.display = 'inline-flex';
        } else {
            (insigna as HTMLElement).style.display = 'none';
        }
    } catch { /* pagina merge și fără insignă */ }
}
